import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { getSendToClientPreview } from '../../api/proposals';

const SendToClientModal = ({ isOpen, onClose, onSend, leadId, proposalId, isLoading }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    if (isOpen && leadId && proposalId) {
      setPreviewLoading(true);
      setPreviewError(null);
      getSendToClientPreview(leadId, proposalId)
        .then((res) => {
          const data = res.data;
          setSubject(data.subject);
          setBody(data.body);
          setClientEmail(data.client_email);
        })
        .catch((err) => {
          setPreviewError(err.message || 'Failed to load email preview');
        })
        .finally(() => {
          setPreviewLoading(false);
        });
    }
  }, [isOpen, leadId, proposalId]);

  const handleSend = () => {
    onSend({
      email_subject: subject.trim(),
      email_body: body.trim(),
    });
  };

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && !previewLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Proposal to Client"
      size="xl"
      closeOnOverlayClick={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={!canSend || isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Email'}
          </Button>
        </>
      }
    >
      {previewLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-3 text-gray-500">Loading email preview...</span>
        </div>
      ) : previewError ? (
        <div className="py-8 text-center">
          <p className="text-red-600 dark:text-red-400">{previewError}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Recipient info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-[#A0A4A8] mb-1">
              To
            </label>
            <div className="px-3 py-2 bg-gray-50 dark:bg-[#1A1C1E] border border-gray-200 dark:border-[#3A3C3E] rounded-lg text-sm text-gray-600 dark:text-[#A0A4A8]">
              {clientEmail}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-[#A0A4A8] mb-1">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#1A1C1E] text-gray-900 dark:text-[#E8E9EA] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors"
              placeholder="Email subject"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-[#A0A4A8] mb-1">
              Email Content
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full px-3 py-2 border border-gray-300 dark:border-[#3A3C3E] rounded-lg text-sm bg-white dark:bg-[#1A1C1E] text-gray-900 dark:text-[#E8E9EA] focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-colors resize-y font-sans leading-relaxed"
              placeholder="Email content..."
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SendToClientModal;
